import React, {useEffect, useState} from 'react';
import {connect} from "react-redux";
import {RootState} from "../../../../../reducers";
import {useTranslation, withTranslation} from "react-i18next";
import PopupModal from "../../../Modal/PopupModal";
import CartButtonComponent from "../CartButton/CartButtonComponent";
import AppConfig from "../../../../services/AppConfig";
import {useNavigate} from "react-router-dom";
import {usePrefix} from "../../../../services/Prefix";
import {
    MenuProductViewType,
    OrderType,
    UpsellApi,
    UpsellEntityApi,
    UpsellVisibilityType
} from "../../../../services/Api/types";
import {
    getAvailabilities,
    getCategories,
    getItemGroups,
    getItems,
    getMenus,
    getPromotions
} from "../../../../services/Menu/reducer";
import PromotionHelper from "../../../../services/Menu/services/Promotion/PromotionHelper";
import {getCart} from "../../../../services/Cart/reducer";
import {getSession} from "../../../../services/Session/reducer";
import StoreUtils from "../../../../services/Store/StoreUtils";
import {getStoreConfig} from "../../../../services/Store/reducer";
import ItemViewHelper from "../../../../services/Menu/services/Item/ItemViewHelper";
import ItemAvailabilityHelper from "../../../../services/Menu/services/Item/ItemAvailabilityHelper";
import MenuCategoryProduct from "../../../../pages/Menu/components/Category/MenuCategoryProduct";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../services/Common/Date/DateService";
import ItemHelper from "../../../../services/Menu/services/Item/ItemHelper";
import CategoryHelper from "../../../../services/Menu/services/Category/CategoryHelper";
import {getParsedSessionOrderType} from "../../../../utils/order";
import IframeWidget from "../../../../utils/IframeWidget";

interface OwnProps {
    show: boolean;
    onHide: () => void;
    upsells: UpsellApi[];
}

type UpsellEntity = {
    upsell: UpsellApi;
    entity: UpsellEntityApi;
    parsedEntity: Record<string, any>;
    category: Record<string, any>;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const UpsellsModal = ({
                          show,
                          onHide,
                          upsells,
                          items,
                          itemGroups,
                          categories,
                          availabilities,
                          promotions,
                          cart,
                          session,
                          menus,
                          config
                      }: Props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const prefix = usePrefix();
    const isMultilevelMenu = StoreUtils.isMultilevelMenu(config);
    const [entities, setEntities] = useState<UpsellEntity[]>([]);
    const orderType = session.order_type as OrderType;
    const orderedItemIds = new Set(cart.items.map(item => item.item_id));

    useEffect(() => {
        handleInit();
    }, []);

    const handleInit = () => {
        if (upsells.every(upsell => upsell.entities.length === 0)) {
            handleHideOnInit();
        }

        const validEntities = getParsedEntities().filter(isEntityAvailable);
        setEntities(validEntities);

        if (validEntities.length === 0) {
            handleHideOnInit();
        }
    }

    const handleHideOnInit = () => {
        onHide();
        handleGoToCart();
        return;
    }

    const getParsedEntity = (entity: UpsellEntityApi) => {
        const entityId = Number(entity.entity_id);
        const availablePromotions = PromotionHelper.getActivePromotionsForCategory(promotions, cart.total);

        if (entity.type === "ITEM") {
            const item = items.find((item: any) => item.id === entityId);

            if (!item) return undefined;

            return ItemViewHelper.parseItem(
                item, availabilities, availablePromotions,
                session.pickup_at, entity.position, cart, orderType,
            );
        }

        const itemGroupForItem = itemGroups.find((itemGroup: Record<string, any>) => itemGroup.id === entityId);
        if (!itemGroupForItem) return undefined;

        return ItemViewHelper.parseItemGroup(
            itemGroupForItem, items, availabilities, availablePromotions,
            session.pickup_at, entity.position, cart, orderType
        );
    }

    const filterEntitiesByUpsellVisibilityForUpsell = (visibilityType: UpsellVisibilityType, entities: UpsellEntity[]) => {
        switch (visibilityType) {
            case UpsellVisibilityType.MISSING: {
                return entities.filter(entity => {
                    if (entity.entity.type === "ITEM_GROUP") {
                        const itemIds: number[] = entity.parsedEntity.items.map((item: Record<string, any>) => item.item_id);

                        return !itemIds.some(itemId => orderedItemIds.has(itemId));
                    }

                    return !orderedItemIds.has(entity.parsedEntity.id);
                });
            }
            case UpsellVisibilityType.NONE_IF_ANY_INCLUDED: {
                const anyEntityInCart = entities.some(entity => {
                    if (entity.entity.type === "ITEM_GROUP") {
                        const itemIds: number[] = entity.parsedEntity.items.map((item: Record<string, any>) => item.item_id);
                        return itemIds.some(itemId => orderedItemIds.has(itemId));
                    }
                    return orderedItemIds.has(entity.parsedEntity.id);
                });

                return anyEntityInCart ? [] : entities;
            }
            case UpsellVisibilityType.ALL:
            default:
                return entities;
        }
    }


    const getParsedEntities = () => {
        const entityMap = new Map<string, UpsellEntity>();
        const sortedUpsells: UpsellApi[] = [...upsells].sort((a, b) => a.priority - b.priority);

        sortedUpsells.forEach(upsell => {
            let validEntities = upsell.entities
                .map((entity) => {
                    let parsedEntity = getParsedEntity(entity);
                    if (!parsedEntity) return undefined;

                    const category = ItemAvailabilityHelper.getCategoryWithNearestAvailableDateForItem(
                        entity.type === "ITEM_GROUP" ? parsedEntity.item : parsedEntity,
                        categories,
                        menus,
                        session,
                        undefined,
                        entity.type === "ITEM_GROUP" ? parsedEntity : undefined,
                    );

                    if (!category) return undefined;

                    if (entity.type === "ITEM_GROUP") {
                        const newItems = parsedEntity.items.filter(
                            (itemGroupItem: Record<string, any>) =>
                                category.entities.some((categoryEntity: Record<string, any>) =>
                                    itemGroupItem.item_id === categoryEntity.entity_id
                                )
                        );

                        if (!newItems.length) return undefined;

                        parsedEntity = {
                            ...parsedEntity,
                            items: newItems,
                            item: newItems[0],
                        };
                    }

                    return {
                        entity,
                        parsedEntity,
                        upsell,
                        category
                    } as UpsellEntity;
                })
                .filter(Boolean) as UpsellEntity[];

            validEntities = filterEntitiesByUpsellVisibilityForUpsell(upsell.visibility_type, validEntities);

            [...validEntities]
                .sort((a, b) => a.entity.position - b.entity.position)
                .forEach(validEntity => {
                    const entityKey = `${validEntity.entity.type}_${validEntity.entity.entity_id}`;
                    const existing = entityMap.get(entityKey);

                    if (!existing) {
                        entityMap.set(entityKey, validEntity);
                        return;
                    }

                    if (validEntity.entity.position < existing.entity.position) {
                        entityMap.set(entityKey, validEntity);
                    }
                });
        });

        return Array.from(entityMap.values()).sort((a, b) => {
            if (a.upsell.priority !== b.upsell.priority) {
                return a.upsell.priority - b.upsell.priority;
            }

            return a.entity.position - b.entity.position;
        });
    }

    const handleGoToCart = (hideIframe?: boolean) => {
        if (hideIframe) IframeWidget.onHide(() => onHide());
        navigate(AppConfig.getLinkWithPrefix('/cart', prefix));
    }

    const handleAddItem = (entity: UpsellEntity) => {
        let back = true;
        if (!!config?.old_settings?.homepage_full) {
            back = false;
        }

        const menuId = menus.find((menu: Record<string, any>) => menu.categories.find((menuCategory: Record<string, any>) => menuCategory.category_id === entity.category.id))?.id;

        if (isMultilevelMenu && !menuId) return;

        const id = entity.entity.type === "ITEM_GROUP" ? entity.parsedEntity.item.id : entity.parsedEntity.id;
        const link = isMultilevelMenu && menuId ? `/menu/${menuId}/add/${id}` : `/menu/add/${id}`;

        navigate(AppConfig.getLinkWithPrefix(link, prefix), {
            state: {
                back,
                categoryIds: entity.category.originalIds,
            }
        });
    }

    const isEntityAvailable = (entity: UpsellEntity) => {
        const {parsedEntity, category} = entity;
        const isEntityAnItemGroup = entity.entity.type === "ITEM_GROUP";
        const item = isEntityAnItemGroup ? parsedEntity.item : parsedEntity;
        const itemGroup = isEntityAnItemGroup ? parsedEntity : undefined;
        const availability = AvailableHelper.getAvailability(availabilities, item.availability_id);
        const isOpen = availability ? HoursHelper.isOpen(availability.hours, DateService.emptyOrNow(session.pickup_at)) && AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)) : true;
        const available = isOpen && !item.disabled;

        if (!category || !category.originalIds || category.originalIds.length < 2) return available;

        const itemFromCategoryItems = ItemHelper.getItemFromCategoryItems(item, itemGroup, category.entities);
        const menusWithCategory = CategoryHelper.getMenusForCategoryByIds(category, menus);
        const menusWithCategoryForItem = itemFromCategoryItems ? menusWithCategory.filter((menu: Record<string, any>) => itemFromCategoryItems.menuAvailabilitiesIds.includes(menu.availability_id)) : [...menusWithCategory];
        const menusWithCategoryForCurrentOrderType = menusWithCategoryForItem.filter((menu: Record<string, any>) => menu[getParsedSessionOrderType(orderType).toLowerCase()] === true);
        const menuAvailabilitiesForCurrentType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForCurrentOrderType.map((menu: Record<string, any>) => menu.availability_id));
        const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForCurrentType.flatMap((availability: Record<string, any>) => availability.hours), DateService.emptyOrNow(session.pickup_at));
        const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForCurrentType.some((availability: Record<string, any>) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)));
        const isCategoryAvailable = isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForCurrentOrderType.length > 0;

        return available && isCategoryAvailable;
    }

    return (
        <PopupModal
            title={t("modules.cart.field.upsells_header.title")}
            show={show}
            onHide={onHide}
            primaryButton={
                <CartButtonComponent goToCart={() => handleGoToCart(true)} menuId={undefined} useOutsideCart/>
            }
            className="upsells-modal"
            bodyContent={
                <div className="menu-items menu-items-horizontal-list">
                    {
                        entities.filter((_, index) => index < 4).map((entity) => (
                                <MenuCategoryProduct viewType={MenuProductViewType.HORIZONTAL}
                                                     product={entity.parsedEntity}
                                                     key={entity.parsedEntity.id}
                                                     category={entity.category}
                                                     promotions={promotions}
                                                     addItem={() => handleAddItem(entity)}
                                                     hideDescription
                                                     showCartQuantity
                                />
                            )
                        )
                    }
                </div>
            }
        />
    )
}
const mapStateToProps = (state: RootState) => ({
    items: getItems(state.menu),
    itemGroups: getItemGroups(state.menu),
    availabilities: getAvailabilities(state.menu),
    promotions: getPromotions(state.menu),
    categories: getCategories(state.menu),
    cart: getCart(state.cart),
    session: getSession(state.session),
    menus: getMenus(state.menu),
    config: getStoreConfig(state.config),
});

const connector = connect(mapStateToProps);
export default withTranslation()(connector(UpsellsModal));